import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { GoogleMap, type GoogleMapProps, useJsApiLoader } from '@react-google-maps/api'
import { type UseLoadScriptOptions } from '@react-google-maps/api/dist/useJsApiLoader'

import {
  MenuFilter,
  type MenuFilterActions,
  type MenuFilterProps,
} from '@/components/molecules/MenuFilter/MenuFilter.tsx'
import { MapTeamPositionContext } from '@/components/organisms/Map/Markers/MapTeamPositionProvider'
import { TeamMarker, type TeamMarkerProps } from '@/components/organisms/Map/Markers/TeamMarker.tsx'
import {
  TransportRequestMarker,
  type TransportRequestMarkerProps,
} from '@/components/organisms/Map/Markers/TransportRequestMarker.tsx'
import { cn } from '@/lib/utils.ts'
import { handleNotAwaitedPromise } from '@/sentry/handleNotAwaitedPromise.ts'

function asPromise<T>(value: T | Promise<T> | void | undefined): Promise<T> | undefined {
  return value instanceof Promise ? value : undefined
}

type PendingTeamStatusFilter = MenuFilterProps<'available' | 'paused' | 'returning'>

export interface MapOptions {
  mapPendingTeamStatusFilterItems?: PendingTeamStatusFilter['menuFilterItems']
}

export interface MapConfiguration {
  googleMapConfiguration: UseLoadScriptOptions
}

export interface MapState {
  mapPendingTeamStatusActiveFilters?: MenuFilterProps['activeFilters']
  activeView: 'all' | 'pending' | 'assigned' | 'allocated' | 'onboarded' | 'completed'
  selectedTransportRequestIds?: string[]
  selectedTeamIds?: string[]
}

export interface GoogleMapActions {
  onMapLoad: GoogleMapProps['onLoad']
  onMapUnmount: GoogleMapProps['onUnmount']
}

export interface MapActions extends MenuFilterActions, GoogleMapActions {}

type TeamMapItem = Omit<TeamMarkerProps, 'status' | 'teamId'> & {
  id: string
  status: 'available' | 'paused' | 'returning'
}

type TransportRequestMapItem = TransportRequestMarkerProps & {
  id: string
}

export interface MapData extends Pick<GoogleMapProps, 'zoom' | 'center'> {
  teams?: TeamMapItem[]
  transportRequests?: TransportRequestMapItem[]
}

export interface MapProps
  extends React.HtmlHTMLAttributes<HTMLDivElement>, MapOptions, MapActions, MapState, MapConfiguration {
  data: MapData
  height?: string
  children?: React.ReactNode
  fitBoundsToVisibleMarkers?: boolean
}

export const Map = React.forwardRef<HTMLDivElement, MapProps>(
  (
    {
      className,
      height = '100vh',
      activeView,
      selectedTransportRequestIds = [],
      selectedTeamIds = [],
      mapPendingTeamStatusFilterItems = [],
      mapPendingTeamStatusActiveFilters,
      data: { center, zoom = 11, teams = [], transportRequests = [] },
      onMenuFilterChanged,
      onMapLoad,
      onMapUnmount,
      googleMapConfiguration,
      children,
      fitBoundsToVisibleMarkers = false,
      ...props
    },
    ref
  ) => {
    const { isLoaded } = useJsApiLoader(googleMapConfiguration)
    const mapRef = useRef<google.maps.Map | null>(null)
    const teamPositions = useContext(MapTeamPositionContext)

    const isTeamVisible = useCallback(
      (id: string, status: TeamMapItem['status']) => {
        if (selectedTeamIds.length > 0) {
          return selectedTeamIds.includes(id)
        }
        if (activeView === 'pending') {
          return (
            mapPendingTeamStatusActiveFilters?.[status] ??
            mapPendingTeamStatusFilterItems.find((item) => item.id === status)?.default ??
            false
          )
        }
        if (selectedTransportRequestIds.length > 0) {
          return false
        }
        return activeView === 'all'
      },
      [selectedTeamIds, activeView, mapPendingTeamStatusActiveFilters, mapPendingTeamStatusFilterItems]
    )

    const isTransportRequestVisible = useCallback(
      (id: string, status: TransportRequestMapItem['status']) => {
        if (selectedTransportRequestIds.length > 0) {
          return selectedTransportRequestIds.includes(id)
        }
        if (selectedTeamIds.length > 0) {
          return false
        }
        return activeView === status || (activeView === 'all' && status !== 'completed')
      },
      [activeView, selectedTeamIds, selectedTransportRequestIds]
    )

    const handleMapLoad = useCallback(
      (map: google.maps.Map) => {
        mapRef.current = map
        handleNotAwaitedPromise(asPromise(onMapLoad?.(map)))
      },
      [onMapLoad]
    )

    const handleMapUnmount = useCallback(
      (map: google.maps.Map) => {
        mapRef.current = null
        handleNotAwaitedPromise(asPromise(onMapUnmount?.(map)))
      },
      [onMapUnmount]
    )

    // Compute visible markers' positions for bounds fitting
    const visiblePositions = useMemo(() => {
      if (!fitBoundsToVisibleMarkers) return []

      const positions: google.maps.LatLngLiteral[] = []

      // Add visible teams' positions
      teams.forEach(({ id, status }) => {
        if (isTeamVisible(id, status)) {
          const pos = teamPositions[id]
          if (pos) {
            // Handle both LatLng and LatLngLiteral types
            if (pos instanceof google.maps.LatLng) {
              positions.push({ lat: pos.lat(), lng: pos.lng() })
            } else {
              positions.push(pos)
            }
          }
        }
      })

      // Add visible transport requests' origins and destinations
      transportRequests.forEach(({ id, status, origin, destination }) => {
        if (isTransportRequestVisible(id, status)) {
          positions.push(origin, destination)
        }
      })

      return positions
    }, [fitBoundsToVisibleMarkers, teams, transportRequests, teamPositions, isTeamVisible, isTransportRequestVisible])

    // Fit bounds when visible markers change
    useEffect(() => {
      if (!fitBoundsToVisibleMarkers || !mapRef.current || visiblePositions.length === 0) return

      const bounds = new google.maps.LatLngBounds()
      visiblePositions.forEach((pos) => bounds.extend(pos))
      mapRef.current.fitBounds(bounds, 50) // 50px padding
    }, [fitBoundsToVisibleMarkers, visiblePositions])

    if (!isLoaded) {
      return null
    }

    return (
      <div
        className={cn('flex h-screen w-full items-center justify-center bg-gray-100 text-gray-900', className)}
        ref={ref}
        {...props}>
        <GoogleMap
          mapContainerStyle={{
            width: '100%',
            height,
          }}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            controlSize: 24,
            maxZoom: 18,
            minZoom: 6,
            gestureHandling: 'greedy',
            mapId: import.meta.env.VITE_GOOGLE_MAP_ID,
          }}
          center={center}
          zoom={zoom}
          onLoad={handleMapLoad}
          onUnmount={handleMapUnmount}>
          {children}
          {teams.map(({ id, status, ...team }) => (
            <TeamMarker
              {...team}
              teamId={id}
              key={`${id}-${status}`}
              status={status}
              visible={isTeamVisible(id, status)}
            />
          ))}
          {transportRequests.map(({ status, ...request }) => (
            <TransportRequestMarker
              {...request}
              key={`${request.id}-${status}`}
              status={status}
              visible={isTransportRequestVisible(request.id, status)}
              selected={selectedTransportRequestIds.includes(request.id)}
              plannedTeam={activeView === 'pending' ? request.plannedTeam : undefined}
            />
          ))}
        </GoogleMap>
        {activeView === 'pending' && selectedTeamIds.length === 0 && (
          <MenuFilter
            className="fixed bottom-[26px] left-3.5"
            menuFilterItems={mapPendingTeamStatusFilterItems}
            activeFilters={mapPendingTeamStatusActiveFilters}
            onMenuFilterChanged={onMenuFilterChanged}
          />
        )}
      </div>
    )
  }
)
